import React, { useEffect, useState } from 'react';

const LoginPage = ({ onLoginSuccess }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Carrega o script do Google Sign-In
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      // Inicializa o botão do Google Sign-In
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'filled_blue',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 300,
          }
        );
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleCredentialResponse = async (response) => {
    setIsLoading(true);
    setError(null);

    try {
      const credential = response.credential;

      // Envia o token para o backend
      const authResponse = await fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ credential }),
      });

      const data = await authResponse.json();

      if (authResponse.ok && data.success) {
        // Login bem-sucedido
        onLoginSuccess(data.user);
      } else {
        // Erro na autenticação
        setError(data.message || 'Erro ao fazer login. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao autenticar:', err);
      setError('Erro de conexão. Verifique se o servidor de autenticação está rodando.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#252722' }}>
      <div className="max-w-md w-full mx-4">
        {/* Card de Login */}
        <div
          className="rounded-2xl p-8 shadow-2xl backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(33, 46, 13, 0.95)',
            boxShadow: 'rgb(0 0 0 / 50%) 0px 0px 32px 0px, inset rgb(32 46 12 / 50%) 0px 0px 32px 0px',
          }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/pickiwi-logo.webp"
              alt="Pickiwi Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">
              Bem-vindo ao Pickiwi Editor
            </h1>
            <p className="text-gray-300 text-sm">
              Faça login com sua conta Google para acessar
            </p>
          </div>

          {/* Mensagem de erro */}
          {error && (
            <div
              className="mb-6 p-4 rounded-lg border border-red-500/30"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
            >
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-red-400 text-sm font-medium mb-1">Erro ao fazer login</p>
                  <p className="text-red-300 text-xs">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="mb-6 text-center">
              <div className="inline-block w-8 h-8 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
              <p className="text-gray-300 text-sm mt-2">Autenticando...</p>
            </div>
          )}

          {/* Botão do Google Sign-In */}
          <div className="flex justify-center mb-6">
            <div id="google-signin-button"></div>
          </div>

          {/* Informações adicionais */}
          <div
            className="mt-6 p-4 rounded-lg border border-blue-500/30"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
          >
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-blue-400 text-sm font-medium mb-1">Acesso restrito</p>
                <p className="text-blue-300 text-xs">
                  Apenas emails autorizados podem acessar o editor. Se você não tem acesso, entre em contato com o administrador.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          Ao fazer login, você concorda com os termos de uso
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
